import { Component, Input, Output, EventEmitter, inject, OnInit, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MenuService } from '../../core/services/menu.service';
import { NavItem } from '../../core/models/menu.model';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
  host: {
    '[class.md:w-72]': '!isCollapsed',
    '[class.md:w-20]': 'isCollapsed',
    '[class.max-md:-translate-x-full]': '!mobileOpen',
    '[class.max-md:translate-x-0]': 'mobileOpen',
    'class': 'fixed inset-y-0 left-0 z-40 flex flex-col bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 transition-all duration-300 ease-in-out md:static shrink-0 w-72 h-screen'
  }
})
export class SidebarComponent implements OnInit {
  @Input() isCollapsed = false;
  @Input() mobileOpen = false;
  @Output() closeMobile = new EventEmitter<void>();

  private readonly router = inject(Router);
  private readonly menuService = inject(MenuService);

  // Keep track of which submenus are expanded
  protected readonly expandedMenus: Record<string, boolean> = {};

  // Ensure sidebar names and layout remain expanded on mobile viewports
  get showNames(): boolean {
    return !this.isCollapsed || window.innerWidth < 768;
  }

  protected readonly menuItems = this.menuService.navItems;

  constructor() {
    // Reactively expand submenus when dynamic menu items are fetched
    effect(() => {
      const items = this.menuItems();
      if (items && items.length > 0) {
        this.expandActiveMenus();
      }
    });
  }

  ngOnInit(): void {
    this.expandActiveMenus();
    // Subscribe to router changes to auto-expand parent submenus on active child routes
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.expandActiveMenus();
    });
  }

  private lastUrl = '';

  private expandActiveMenus(): void {
    const currentUrl = this.router.url;
    const items = this.menuItems();
    for (const item of items) {
      if (item.children) {
        const wasActive = this.lastUrl.startsWith(item.route) ||
          item.children.some(child => this.lastUrl === child.route || this.lastUrl.startsWith(child.route));
        const isActive = currentUrl.startsWith(item.route) ||
          item.children.some(child => currentUrl === child.route || currentUrl.startsWith(child.route));
        
        // Only auto-expand if it is newly active (navigating from another section)
        if (isActive && !wasActive) {
          this.expandedMenus[item.label] = true;
        }
      }
    }
    this.lastUrl = currentUrl;
  }

  protected toggleSubmenu(label: string): void {
    this.expandedMenus[label] = !this.expandedMenus[label];
  }

  protected isSubmenuActive(item: NavItem): boolean {
    if (this.router.url.startsWith(item.route)) {
      return true;
    }
    if (item.children) {
      return item.children.some(child => this.router.url === child.route || this.router.url.startsWith(child.route));
    }
    return false;
  }
}

